import React, { useState } from "react";
import { usePatient } from "../../contexts";
import Cookies from "js-cookie";
import { AiOutlinePlus } from "react-icons/ai";
import PayReferralLabRequest from "../modals/PayReferralLabRequest";
import Spinner from "../UI/Spinner";
import TableSkeleton from "./TableSkeleton";
import { del } from "../../utility/fetch";
import notification from "../../utility/notification";

function QueuedReferralTable({ data, currentPage, itemsPerPage, loading, getProcedures, renderTabContent, isApproval, requestType }) {
    const { setPatientId, setPatientName, setPatientInfo, nurseRoles } = usePatient();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [patientData, setData] = useState([]);

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const addToQueue = (data) => {
        setData(data);
        setIsModalOpen(true);
    };

    const handleDelete = async (id) => {
        try {
            let res = await del(`/referredpatient/${id}/delete-referred-patient`);
            if (res) {
                notification({ message: 'Referral lab request deleted successfully', type: 'success' });
                getProcedures();
            } else {
                notification({ message: 'Failed to delete referral lab request', type: 'error' });
            }
        } catch (error) {
            notification({ message: 'An error occurred while deleting the referral lab request', type: 'error' });
        }
    };

    const continueUpdate = (data) => {
        setPatientInfo(data);
    };

    // helper to render approval status cell
    const renderApprovalStatus = (row) => {
        let value = row?.isApproved;
        if (value === undefined && Array.isArray(row?.testRequests) && row.testRequests.length > 0) {
            const approvals = row.testRequests.map(r => r.isApproved);
            if (approvals.includes(true)) value = true;
            else if (approvals.includes(false)) value = false;
            else value = null;
        }

        const styleBase = { padding: '6px 10px', borderRadius: 6, display: 'inline-block', fontWeight: 700, fontSize: 12 };
        if (value === null || value === undefined) {
            return <td><span style={{ ...styleBase, color: '#555', background: '#f4f4f4' }}>NEW</span></td>;
        }
        if (value === true) {
            return <td><span style={{ ...styleBase, color: '#155724', background: '#d4edda' }}>APPROVED</span></td>;
        }
        return <td><span style={{ ...styleBase, color: '#721c24', background: '#f8d7da' }}>DECLINED</span></td>;
    };

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
        return (
            <div className="w-100">
                <h2 className="m-t-20 m-b-20">REFERRAL PATIENTS - QUEUED</h2>
                <div className="flex flex-col gap-12">
                    {loading ? (
                        <TableSkeleton />
                    ) : (
                        Array.isArray(data) && data?.map((row) => {
                            let approvalStatus = 'NEW';
                            let approvalColor = '#555';
                            let approvalBg = '#f4f4f4';
                            let value = row?.isApproved;
                            if (value === undefined && Array.isArray(row?.testRequests) && row.testRequests.length > 0) {
                                const approvals = row.testRequests.map(r => r.isApproved);
                                if (approvals.includes(true)) value = true;
                                else if (approvals.includes(false)) value = false;
                                else value = null;
                            }
                            if (value === true) { approvalStatus = 'APPROVED'; approvalColor = '#155724'; approvalBg = '#d4edda'; }
                            else if (value === false) { approvalStatus = 'DECLINED'; approvalColor = '#721c24'; approvalBg = '#f8d7da'; }

                            return (
                                <div key={row?.patientId || row?.id} className="card shadow-sm m-b-20" style={{ borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }} onClick={() => continueUpdate(row)}>
                                    <div className="flex flex-h-between flex-v-center" style={{ padding: '15px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #eee' }}>
                                        <h4 className="m-0" style={{ fontSize: '16px', fontWeight: '600', color: '#333' }}>{row?.patient?.firstName || row?.firstName} {row?.patient?.lastName || row?.lastName}</h4>
                                        <span style={{ fontSize: '12px', color: '#666' }}>ID: {row?.id}</span>
                                    </div>
                                    <div style={{ padding: '15px' }}>
                                        <div className="m-b-5" style={{ fontSize: '14px' }}><strong>Email:</strong> {row?.patient?.email || row?.email}</div>
                                        <div className="m-b-5" style={{ fontSize: '14px' }}><strong>Mobile:</strong> {row?.patient?.phoneNumber || row?.phoneNumber}</div>
                                        <div className="m-b-10" style={{ fontSize: '14px' }}><strong>Gender:</strong> {row?.patient?.gender || row?.gender}</div>
                                        <div className="flex flex-h-between flex-v-center">
                                            <span style={{ fontSize: '12px', color: '#888' }}>{new Date(row?.createdAt).toLocaleDateString()}</span>
                                            <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', color: approvalColor, backgroundColor: approvalBg }}>{approvalStatus}</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-h-end flex-v-center" style={{ padding: '10px 15px', backgroundColor: '#f8f9fa', borderTop: '1px solid #eee' }}>
                                        <AiOutlinePlus size={22} onClick={(e) => { e.stopPropagation(); addToQueue(row); }} className="pointer" title="Approve/Process" />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                {isModalOpen && <PayReferralLabRequest isApproval={isApproval} closeModal={closeModal} patientData={patientData} getProcedures={getProcedures} requestType={requestType} />}
            </div>
        );
    }

    return (
        <div className="w-100">
            <div className="w-100 none-flex-item m-t-40">
                <h2>REFERRAL PATIENTS - QUEUED FOR LAB</h2>
                <table className="bordered-table m-t-20">
                    <thead className="border-top-none">
                        <tr className="border-top-none">
                            <th className="center-text">Lab Id</th>
                            <th className="center-text">First Name</th>
                            <th className="center-text">Last Name</th>
                            <th className="center-text">Email</th>
                            <th className="center-text">Mobile Number</th>
                            <th className="center-text">Gender</th>
                            <th className="center-text">Created On</th>
                            <th className="center-text">Approval Status</th>
                            <th className="center-text">Action</th>
                        </tr>
                    </thead>

                    {loading ? (
                        <TableSkeleton primary={5} secondary={9} />
                    ) : (
                        <tbody className="white-bg view-det-pane">
                            {Array.isArray(data) &&
                                data?.map((row, index) => (
                                    <tr className="hovers" onClick={() => continueUpdate(row)} key={row?.patientId || row?.id}>
                                        <td>{row?.id}</td>
                                        <td>
                                            <div>
                                                {row?.patient?.firstName || row?.firstName}
                                            </div>
                                        </td>
                                        <td>{row?.patient?.lastName || row?.lastName}</td>
                                        <td>{row?.patient?.email || row?.email}</td>
                                        <td>{row?.patient?.phoneNumber || row?.phoneNumber}</td>
                                        <td>{row?.patient?.gender || row?.gender}</td>
                                        <td>{new Date(row?.createdAt).toLocaleDateString()}</td>
                                        {renderApprovalStatus(row)}
                                        <td className="flex flex-h-center flex-v-center">
                                            <AiOutlinePlus size={25} onClick={() => addToQueue(row)} className="m-r-20 pointer" title="Approve/Process" />
                                        </td>
                                    </tr>
                                ))}
                        </tbody>
                    )}
                </table>
            </div>

            {isModalOpen &&
                <PayReferralLabRequest
                    isApproval={isApproval}
                    closeModal={closeModal}
                    patientData={patientData}
                    getProcedures={getProcedures}
                    requestType={requestType}
                />
            }
        </div>
    );
}

export default QueuedReferralTable;